import { useMemo, useState } from "react";
import {
  Baby,
  CheckCircle2,
  Clipboard,
  FileDown,
  Info,
  Loader2,
  Save,
  Smile,
  Sparkles,
  Stethoscope,
  UserRound,
} from "lucide-react";

import { doc, serverTimestamp, updateDoc } from "../../access/firestore";
import { db } from "../../firebase";
import { useClinic } from "../../context/ClinicContext";

const yesNoOptions = [
  { value: "no", label: "No" },
  { value: "si", label: "Sí" },
  { value: "no-evaluado", label: "No evaluado" },
];

const selectOptions = {
  higiene: [
    { value: "buena", label: "Buena" },
    { value: "regular", label: "Regular" },
    { value: "mala", label: "Mala" },
    { value: "no-evaluada", label: "No evaluada" },
  ],
  saliva: [
    { value: "normal", label: "Normal" },
    { value: "reducida", label: "Reducida" },
    { value: "severamente-reducida", label: "Severamente reducida" },
    { value: "no-evaluada", label: "No evaluada" },
  ],
  assessment: [
    { value: "baseline", label: "Evaluación inicial" },
    { value: "recall-3", label: "Control a 3 meses" },
    { value: "recall-6", label: "Control a 6 meses" },
  ],
  level: [
    { value: "no-evaluado", label: "No evaluado" },
    { value: "bajo", label: "Bajo" },
    { value: "medio", label: "Medio" },
    { value: "alto", label: "Alto" },
  ],
};

const question = (id, label, category, extra = {}) => ({
  id,
  label,
  category,
  type: "boolean",
  defaultValue: "no",
  options: yesNoOptions,
  ...extra,
});

const adultQuestions = [
  {
    title: "Indicadores de enfermedad",
    helper: "Cualquier indicador positivo requiere valoración clínica y puede elevar el riesgo.",
    items: [
      question("visible-cavity", "Cavidad visible o lesión radiográfica que penetra dentina", "disease"),
      question("approximal-enamel", "Lesión proximal en esmalte observada radiográficamente", "disease"),
      question("white-spots", "Manchas blancas activas en superficies lisas", "disease"),
      question("recent-restorations", "Restauraciones realizadas en los últimos 3 años", "disease"),
    ],
  },
  {
    title: "Factores biológicos y ambientales de riesgo",
    items: [
      question("bacteria-levels", "MS y LB en nivel medio o alto por cultivo", "risk"),
      question("heavy-plaque", "Placa visible abundante en los dientes", "risk"),
      question("frequent-snacking", "Consume alimentos o bebidas entre comidas más de 3 veces al día", "risk"),
      question("deep-pits", "Presenta fosas y fisuras profundas", "risk"),
      question("recreational-drugs", "Uso de drogas recreativas", "risk"),
      question("inadequate-saliva", "Flujo salival inadecuado por observación o medición", "risk"),
      question("saliva-reducing", "Medicamentos, radiación o enfermedad sistémica que reducen la saliva", "risk"),
      question("exposed-roots", "Raíces dentales expuestas", "risk"),
      question("orthodontic-appliances", "Utiliza aparatología ortodóncica", "risk"),
    ],
  },
  {
    title: "Factores protectores",
    items: [
      question("fluoridated-water", "Vive o trabaja en una comunidad con agua fluorada", "protective"),
      question("fluoride-once", "Usa pasta dental con flúor al menos una vez al día", "protective"),
      question("fluoride-twice", "Usa pasta dental con flúor dos veces al día o más", "protective"),
      question("high-fluoride", "Usa pasta dental de 5,000 ppm de flúor", "protective"),
      question("fluoride-varnish", "Recibió barniz de flúor en los últimos 6 meses", "protective"),
      question("fluoride-rinse", "Usa enjuague diario de fluoruro de sodio al 0.05%", "protective"),
      question("chlorhexidine", "Usa clorhexidina al 0.12% durante 7 días al mes, según indicación", "protective"),
      question("normal-saliva", "Presenta función salival normal", "protective"),
    ],
  },
];

const pediatricQuestions = [
  {
    title: "Factores biológicos y ambientales",
    items: [
      question("caregiver-decay", "La madre o cuidador principal tuvo caries activa en los últimos 12 meses", "risk"),
      question("non-water-bottle", "Usa biberón con líquidos distintos de agua, leche simple o fórmula simple", "risk"),
      question("continuous-bottle", "Tiene uso continuo del biberón", "risk"),
      question("bedtime-bottle", "Duerme con el biberón o toma pecho a libre demanda durante la noche", "risk"),
      question("child-snacking", "Consume azúcares, almidones cocidos o bebidas azucaradas entre comidas más de 3 veces al día", "risk"),
      question("child-saliva-reducing", "Tiene medicamentos, enfermedad o condición genética que reduce la saliva", "risk"),
      question("special-needs", "Tiene necesidades especiales de atención médica o dificultades del desarrollo que afectan el cuidado bucal", "risk"),
      question("caregiver-context", "Existen barreras importantes de comprensión, apoyo o acceso para realizar el cuidado bucal", "risk"),
    ],
  },
  {
    title: "Factores protectores",
    items: [
      question("child-fluoridated-community", "Vive en una comunidad fluorada o recibe suplemento de flúor indicado", "protective"),
      question("child-fluoridated-water", "Toma agua fluorada, por ejemplo agua de la llave", "protective"),
      question("child-fluoride-once", "Cepilla sus dientes con pasta fluorada al menos una vez al día", "protective"),
      question("child-fluoride-twice", "Cepilla sus dientes con pasta fluorada al menos dos veces al día", "protective"),
      question("child-varnish", "Recibió barniz de flúor en los últimos 6 meses", "protective"),
      question("caregiver-xylitol", "El cuidador usa chicle o pastillas de xilitol 2–4 veces al día", "protective"),
    ],
  },
  {
    title: "Indicadores clínicos",
    helper: "Registrar después de la exploración clínica del menor.",
    items: [
      question("child-white-spots", "Hay manchas blancas, defectos del esmalte o caries visibles", "disease"),
      question("child-restorations", "Hay restauraciones por experiencia previa de caries", "disease"),
      question("child-plaque-bleeding", "La placa es evidente o las encías sangran fácilmente", "risk"),
      question("child-inadequate-saliva", "Se observa flujo salival inadecuado", "risk"),
    ],
  },
];

const PROFILE_DEFINITIONS = {
  adulto: { label: "Adulto", age: "18 años o más", icon: UserRound, questions: adultQuestions },
  infantil: { label: "Infantil", age: "6 a 17 años", icon: Smile, questions: adultQuestions },
  pediatrico: { label: "Pediátrico", age: "0 a 5 años", icon: Baby, questions: pediatricQuestions },
};

const allQuestions = definition => definition.questions.flatMap(section => section.items);

const createInitialAnswers = definition => {
  const answers = {};
  allQuestions(definition).forEach(item => {
    answers[item.id] = item.defaultValue;
  });
  answers.assessmentMoment = "baseline";
  answers.hygiene = "no-evaluada";
  answers.salivaFunction = "no-evaluada";
  answers.salivaFlow = "";
  answers.snacksPerDay = "";
  answers.msLevel = "no-evaluado";
  answers.lbLevel = "no-evaluado";
  answers.testDate = "";
  answers.remineralizationNotes = "";
  answers.caregiverMsLevel = "no-evaluado";
  answers.caregiverLbLevel = "no-evaluado";
  answers.caregiverSalivaFlow = "";
  answers.clinicalNotes = "";
  answers.selfManagementGoals = "";
  return answers;
};

const initialResponses = Object.fromEntries(
  Object.entries(PROFILE_DEFINITIONS).map(([key, definition]) => [key, createInitialAnswers(definition)]),
);

const isYes = value => value === "si";

const getRiskState = ({ diseaseCount, riskCount, protectiveCount, salivaFunction }) => {
  const salivaryRisk = salivaFunction === "severamente-reducida";
  if (salivaryRisk && (diseaseCount > 0 || riskCount > 0)) return { label: "Riesgo extremo", badge: "Prioridad clínica", tone: "extreme" };
  if (diseaseCount > 0 || riskCount >= 4) return { label: "Riesgo alto", badge: "Intervenir", tone: "high" };
  if (riskCount >= 2 || protectiveCount <= 2) return { label: "Riesgo moderado", badge: "Revisar", tone: "moderate" };
  return { label: "Riesgo bajo", badge: "Controlado", tone: "low" };
};

export default function CambraPanel({ paciente }) {
  const { clinicId } = useClinic();
  const [profile, setProfile] = useState("adulto");
  const [responses, setResponses] = useState(initialResponses);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");

  const definition = PROFILE_DEFINITIONS[profile];
  const profileResponses = responses[profile];
  const currentQuestions = allQuestions(definition);
  const patientName = paciente?.nombre || paciente?.name || "Paciente sin nombre";

  const result = useMemo(() => {
    const diseaseCount = currentQuestions.filter(item => item.category === "disease" && isYes(profileResponses[item.id])).length;
    const riskCount = currentQuestions.filter(item => item.category === "risk" && isYes(profileResponses[item.id])).length;
    const protectiveCount = currentQuestions.filter(item => item.category === "protective" && isYes(profileResponses[item.id])).length;
    const answered = currentQuestions.filter(item => profileResponses[item.id] && profileResponses[item.id] !== "no-evaluado").length;
    const risk = getRiskState({ diseaseCount, riskCount, protectiveCount, salivaFunction: profileResponses.salivaFunction });
    const score = Math.max(0, Math.min(100, Math.round(35 + diseaseCount * 14 + riskCount * 5 - protectiveCount * 4 + (profileResponses.hygiene === "mala" ? 9 : profileResponses.hygiene === "regular" ? 4 : 0))));
    return { diseaseCount, riskCount, protectiveCount, answered, score, ...risk };
  }, [currentQuestions, profileResponses]);

  const updateAnswer = (id, value) => {
    setSaveMessage("");
    setResponses(current => ({ ...current, [profile]: { ...current[profile], [id]: value } }));
  };

  const saveEvaluation = async () => {
    if (!clinicId || !paciente?.id) return;
    setSaving(true);
    setSaveMessage("");
    try {
      await updateDoc(doc(db, "clinics", clinicId, "pacientes", paciente.id), {
        cambraEvaluation: {
          profile,
          profileLabel: definition.label,
          ageRange: definition.age,
          responses: profileResponses,
          result: {
            label: result.label,
            score: result.score,
            diseaseCount: result.diseaseCount,
            riskCount: result.riskCount,
            protectiveCount: result.protectiveCount,
            answered: result.answered,
          },
          updatedAt: serverTimestamp(),
        },
      });
      setSaveMessage("Evaluación guardada en el expediente del paciente.");
    } catch (error) {
      console.error("Error guardando evaluación CAMBRA:", error);
      setSaveMessage("No se pudo guardar la evaluación. Inténtalo nuevamente.");
    } finally {
      setSaving(false);
    }
  };

  const copySummary = async () => {
    const summary = `CAMBRA — ${definition.label} (${definition.age})\nResultado sugerido: ${result.label}\nIndicadores de enfermedad: ${result.diseaseCount}\nFactores de riesgo: ${result.riskCount}\nFactores protectores: ${result.protectiveCount}`;
    try {
      await navigator.clipboard.writeText(summary);
      setSaveMessage("Resumen copiado al portapapeles.");
    } catch (error) {
      console.error("No se pudo copiar el resumen CAMBRA:", error);
      setSaveMessage("No se pudo copiar el resumen.");
    }
  };

  return (
    <section className="vd-cambra" aria-label="Evaluación CAMBRA">
      <style>{`
        .vd-cambra { color:#102a43; }
        .vd-cambra__header { display:flex; align-items:flex-start; justify-content:space-between; gap:18px; margin-bottom:18px; }
        .vd-cambra__eyebrow { display:flex; align-items:center; gap:8px; margin:0 0 6px; color:#2672d9; font-size:11px; font-weight:800; letter-spacing:.1em; text-transform:uppercase; }
        .vd-cambra__eyebrow svg { color:#23aebd; }
        .vd-cambra h1 { margin:0; font-size:26px; line-height:1.15; letter-spacing:-.04em; }
        .vd-cambra__lead { max-width:700px; margin:8px 0 0; color:#6f8294; font-size:13px; line-height:1.55; }
        .vd-cambra__patient { display:flex; align-items:center; gap:9px; flex:0 0 auto; padding:10px 12px; border:1px solid #dce8f4; border-radius:13px; background:#f8fbff; color:#46647e; font-size:11px; font-weight:750; }
        .vd-cambra__avatar { display:grid; width:30px; height:30px; place-items:center; border-radius:50%; background:#dff4f2; color:#17677b; font-size:10px; font-weight:800; }
        .vd-cambra__selector-label { margin:0 0 8px; color:#7890a5; font-size:10px; font-weight:850; letter-spacing:.1em; text-transform:uppercase; }
        .vd-cambra__selector { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:12px; }
        .vd-cambra__selector button { display:inline-flex; align-items:center; justify-content:center; gap:8px; min-height:42px; padding:10px 14px; border:1px solid #d7e4f0; border-radius:11px; background:#fff; color:#557087; cursor:pointer; font-size:12px; font-weight:750; transition:all .18s ease; }
        .vd-cambra__selector button:hover { border-color:#8fb9e6; color:#2672d9; }
        .vd-cambra__selector button[aria-pressed="true"] { border-color:#2672d9; background:linear-gradient(135deg,#2672d9,#123f81); color:#fff; box-shadow:0 5px 14px rgba(38,114,217,.2); }
        .vd-cambra__notice { display:flex; align-items:flex-start; gap:9px; margin-bottom:16px; padding:11px 13px; border:1px solid #d5eee8; border-radius:12px; background:#f1fbf7; color:#3b776b; font-size:11px; line-height:1.45; }
        .vd-cambra__notice svg { flex:0 0 auto; margin-top:1px; color:#159a75; }
        .vd-cambra__meta { display:grid; grid-template-columns:minmax(180px,240px) minmax(220px,1fr); gap:12px; margin-bottom:16px; }
        .vd-cambra__field { display:grid; gap:6px; color:#7890a5; font-size:10px; font-weight:800; }
        .vd-cambra__field select, .vd-cambra__field input, .vd-cambra__field textarea { width:100%; box-sizing:border-box; min-height:38px; padding:9px 10px; border:1px solid #d7e4f0; border-radius:9px; background:#fff; color:#284861; font:inherit; font-size:12px; font-weight:500; }
        .vd-cambra__field textarea { min-height:72px; resize:vertical; }
        .vd-cambra__measurement-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:10px; margin-top:12px; }
        .vd-cambra__split { display:grid; grid-template-columns:minmax(0,1.08fr) minmax(300px,.92fr); gap:16px; align-items:start; }
        .vd-cambra__card { overflow:hidden; border:1px solid #dbe6f0; border-radius:17px; background:#fff; box-shadow:0 8px 20px rgba(16,42,67,.045); }
        .vd-cambra__card-header { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:15px 17px; border-bottom:1px solid #edf2f7; }
        .vd-cambra__card-header h2 { margin:0; font-size:14px; letter-spacing:-.02em; }
        .vd-cambra__progress { color:#7590a4; font-size:10px; font-weight:750; white-space:nowrap; }
        .vd-cambra__section { padding:14px 17px 3px; border-bottom:1px solid #edf2f7; }
        .vd-cambra__section:last-child { border-bottom:0; }
        .vd-cambra__section-title { margin:0; color:#173d67; font-size:12px; }
        .vd-cambra__section-helper { margin:4px 0 8px; color:#8a9bab; font-size:10px; line-height:1.4; }
        .vd-cambra__question { display:grid; grid-template-columns:25px minmax(0,1fr) minmax(116px,138px); align-items:center; gap:9px; min-height:52px; border-bottom:1px solid #f0f4f7; color:#496379; font-size:11px; line-height:1.35; }
        .vd-cambra__question:last-child { border-bottom:0; }
        .vd-cambra__question select, .vd-cambra__question input { width:100%; box-sizing:border-box; min-height:32px; padding:6px 7px; border:1px solid #d7e4f0; border-radius:8px; background:#fff; color:#41627d; font:inherit; font-size:10px; }
        .vd-cambra__question input[type="number"] { text-align:right; }
        .vd-cambra__qtag { display:inline-flex; justify-content:center; width:22px; padding:3px 0; border-radius:7px; background:#eef5ff; color:#4275bd; font-size:9px; font-weight:850; }
        .vd-cambra__result { position:sticky; top:18px; }
        .vd-cambra__result-top { padding:18px; background:linear-gradient(135deg,#123e78,#2672d9); color:#fff; }
        .vd-cambra__result-top p { margin:0; color:#cfe4ff; font-size:10px; font-weight:800; letter-spacing:.1em; text-transform:uppercase; }
        .vd-cambra__risk { display:flex; align-items:center; justify-content:space-between; gap:10px; margin-top:8px; }
        .vd-cambra__risk strong { font-size:19px; letter-spacing:-.03em; }
        .vd-cambra__risk-badge { padding:4px 8px; border:1px solid rgba(255,255,255,.3); border-radius:999px; background:rgba(255,255,255,.14); font-size:10px; font-weight:800; }
        .vd-cambra__score { height:7px; margin-top:14px; overflow:hidden; border-radius:20px; background:rgba(255,255,255,.24); }
        .vd-cambra__score span { display:block; height:100%; border-radius:inherit; background:#ffd479; transition:width .2s ease; }
        .vd-cambra__score-label { display:flex; justify-content:space-between; margin-top:7px; color:#d5e9ff; font-size:10px; }
        .vd-cambra__result-body { padding:17px; }
        .vd-cambra__summary { display:flex; gap:9px; margin-bottom:16px; }
        .vd-cambra__summary div { flex:1; padding:10px; border-radius:11px; background:#f7fafc; }
        .vd-cambra__summary b { display:block; font-size:17px; }
        .vd-cambra__summary span { display:block; margin-top:3px; color:#8193a3; font-size:9px; }
        .vd-cambra__result-body h3 { margin:0 0 9px; font-size:13px; }
        .vd-cambra__summary-list { display:grid; gap:9px; margin:0 0 16px; padding:0; list-style:none; }
        .vd-cambra__summary-list li { display:flex; align-items:flex-start; gap:8px; color:#557087; font-size:11px; line-height:1.4; }
        .vd-cambra__summary-list svg { flex:0 0 auto; margin-top:1px; color:#159a75; }
        .vd-cambra__insight { display:flex; align-items:flex-start; gap:9px; margin-bottom:16px; padding:11px; border:1px solid #e4eef8; border-radius:12px; background:#f7fbff; color:#4e6b82; font-size:11px; line-height:1.4; }
        .vd-cambra__insight svg { flex:0 0 auto; color:#2672d9; }
        .vd-cambra__insight strong { display:block; margin-bottom:3px; color:#173d67; }
        .vd-cambra__steps { display:grid; gap:7px; margin-bottom:16px; }
        .vd-cambra__step { display:flex; align-items:center; gap:9px; color:#557087; font-size:11px; }
        .vd-cambra__step b { display:grid; width:22px; height:22px; place-items:center; border-radius:50%; background:#eaf4ff; color:#2672d9; font-size:10px; }
        .vd-cambra__actions { display:flex; flex-wrap:wrap; gap:8px; }
        .vd-cambra__actions button { display:inline-flex; align-items:center; justify-content:center; gap:7px; min-height:38px; padding:9px 12px; border-radius:10px; cursor:pointer; font-size:11px; font-weight:800; }
        .vd-cambra__primary { border:1px solid #2672d9; background:#2672d9; color:#fff; }
        .vd-cambra__primary:disabled { opacity:.65; cursor:wait; }
        .vd-cambra__secondary { border:1px solid #d8e4ef; background:#fff; color:#41627d; }
        .vd-cambra__save-message { width:100%; margin:2px 0 0; color:#557087; font-size:10px; }
        @media (max-width: 900px) {
          .vd-cambra__header { flex-direction:column; }
          .vd-cambra__patient { width:100%; box-sizing:border-box; }
          .vd-cambra__meta { grid-template-columns:1fr; }
          .vd-cambra__measurement-grid { grid-template-columns:1fr; }
          .vd-cambra__split { grid-template-columns:1fr; }
          .vd-cambra__result { position:static; }
        }
        @media (max-width: 560px) {
          .vd-cambra h1 { font-size:22px; }
          .vd-cambra__selector { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); }
          .vd-cambra__selector button { padding:9px 6px; font-size:10px; }
          .vd-cambra__selector button svg { display:none; }
          .vd-cambra__card-header { align-items:flex-start; flex-direction:column; gap:5px; }
          .vd-cambra__question { grid-template-columns:25px minmax(0,1fr); padding:7px 0; }
          .vd-cambra__question select, .vd-cambra__question input { grid-column:2; }
        }
      `}</style>

      <div className="vd-cambra__header">
        <div>
          <p className="vd-cambra__eyebrow"><Sparkles size={14} /> Evaluación preventiva</p>
          <h1>CAMBRA</h1>
          <p className="vd-cambra__lead">Identifica el riesgo de caries y convierte las respuestas del paciente en un plan preventivo claro.</p>
        </div>
        <div className="vd-cambra__patient"><span className="vd-cambra__avatar">{patientName.slice(0, 2).toUpperCase()}</span><span>{patientName}</span></div>
      </div>

      <p className="vd-cambra__selector-label">Tipo de evaluación</p>
      <div className="vd-cambra__selector" role="group" aria-label="Tipo de evaluación CAMBRA">
        {Object.entries(PROFILE_DEFINITIONS).map(([key, item]) => {
          const Icon = item.icon;
          return <button key={key} type="button" aria-pressed={profile === key} onClick={() => { setProfile(key); setSaveMessage(""); }}><Icon size={16} /> {item.label} · {item.age}</button>;
        })}
      </div>

      <div className="vd-cambra__notice"><Info size={15} /><span>El formulario cambia según la edad. La clasificación mostrada es una sugerencia basada en las respuestas; la determinación final corresponde al profesional y a la valoración clínica.</span></div>

      <div className="vd-cambra__meta">
        <label className="vd-cambra__field">Momento de evaluación<select value={profileResponses.assessmentMoment} onChange={event => updateAnswer("assessmentMoment", event.target.value)}>{selectOptions.assessment.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
        <label className="vd-cambra__field">Observaciones clínicas generales<textarea value={profileResponses.clinicalNotes} onChange={event => updateAnswer("clinicalNotes", event.target.value)} placeholder="Anota hallazgos relevantes para interpretar el balance de caries..." /></label>
      </div>

      <div className="vd-cambra__split">
        <section className="vd-cambra__card" aria-labelledby="cambra-cuestionario-title">
          <div className="vd-cambra__card-header"><h2 id="cambra-cuestionario-title">Cuestionario CAMBRA · {definition.label}</h2><span className="vd-cambra__progress">{result.answered} de {currentQuestions.length} respondidas</span></div>
          {definition.questions.map(section => <div className="vd-cambra__section" key={section.title}><h3 className="vd-cambra__section-title">{section.title}</h3>{section.helper && <p className="vd-cambra__section-helper">{section.helper}</p>}{section.items.map((item, index) => <label className="vd-cambra__question" key={item.id}><span className="vd-cambra__qtag">{String(index + 1).padStart(2, "0")}</span><span>{item.label}</span><select value={profileResponses[item.id] || item.defaultValue} onChange={event => updateAnswer(item.id, event.target.value)} aria-label={item.label}>{item.options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>)}</div>)}
          <div className="vd-cambra__section">
            <h3 className="vd-cambra__section-title">Datos complementarios</h3>
            <label className="vd-cambra__question"><span className="vd-cambra__qtag">A1</span><span>¿Cómo describirías la higiene bucal diaria?</span><select value={profileResponses.hygiene} onChange={event => updateAnswer("hygiene", event.target.value)}>{selectOptions.higiene.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
            <label className="vd-cambra__question"><span className="vd-cambra__qtag">A2</span><span>Función salival observada o medida</span><select value={profileResponses.salivaFunction} onChange={event => updateAnswer("salivaFunction", event.target.value)}>{selectOptions.saliva.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
            <div className="vd-cambra__measurement-grid">
              <label className="vd-cambra__field">MS / bacterias<select value={profileResponses.msLevel} onChange={event => updateAnswer("msLevel", event.target.value)}>{selectOptions.level.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
              <label className="vd-cambra__field">LB / bacterias<select value={profileResponses.lbLevel} onChange={event => updateAnswer("lbLevel", event.target.value)}>{selectOptions.level.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
              <label className="vd-cambra__field">Flujo salival, ml/min<input type="number" min="0" step="0.01" value={profileResponses.salivaFlow} onChange={event => updateAnswer("salivaFlow", event.target.value)} placeholder="Opcional" /></label>
              <label className="vd-cambra__field">Fecha de pruebas<input type="date" value={profileResponses.testDate} onChange={event => updateAnswer("testDate", event.target.value)} /></label>
            </div>
            {profile === "pediatrico" && <div className="vd-cambra__measurement-grid"><label className="vd-cambra__field">MS del cuidador<select value={profileResponses.caregiverMsLevel} onChange={event => updateAnswer("caregiverMsLevel", event.target.value)}>{selectOptions.level.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label><label className="vd-cambra__field">LB del cuidador<select value={profileResponses.caregiverLbLevel} onChange={event => updateAnswer("caregiverLbLevel", event.target.value)}>{selectOptions.level.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label><label className="vd-cambra__field">Flujo del cuidador, ml/min<input type="number" min="0" step="0.01" value={profileResponses.caregiverSalivaFlow} onChange={event => updateAnswer("caregiverSalivaFlow", event.target.value)} placeholder="Opcional" /></label></div>}
            <label className="vd-cambra__question"><span className="vd-cambra__qtag">A3</span><span>Frecuencia de colaciones o bebidas entre comidas, por día</span><input type="number" min="0" step="1" value={profileResponses.snacksPerDay} onChange={event => updateAnswer("snacksPerDay", event.target.value)} placeholder="Opcional" /></label>
            {profile === "pediatrico" && <label className="vd-cambra__field" style={{ marginTop: 12 }}>Nueva remineralización desde la última revisión<textarea value={profileResponses.remineralizationNotes} onChange={event => updateAnswer("remineralizationNotes", event.target.value)} placeholder="Anota las piezas o superficies observadas..." /></label>}
            <label className="vd-cambra__field" style={{ marginTop: 12 }}>Metas de autocuidado<textarea value={profileResponses.selfManagementGoals} onChange={event => updateAnswer("selfManagementGoals", event.target.value)} placeholder="Ej. cepillado nocturno, reducir bebidas azucaradas, usar hilo dental..." /></label>
          </div>
        </section>

        <aside className="vd-cambra__card vd-cambra__result" aria-live="polite" aria-label="Resultado automático CAMBRA">
          <div className="vd-cambra__result-top"><p>Clasificación sugerida</p><div className="vd-cambra__risk"><strong>{result.label}</strong><span className="vd-cambra__risk-badge">{result.badge}</span></div><div className="vd-cambra__score"><span style={{ width: `${result.score}%` }} /></div><div className="vd-cambra__score-label"><span>Balance orientativo</span><strong>{result.score}/100</strong></div></div>
          <div className="vd-cambra__result-body"><div className="vd-cambra__summary"><div><b>{result.diseaseCount}</b><span>indicadores de enfermedad</span></div><div><b>{result.riskCount}</b><span>factores de riesgo</span></div><div><b>{result.protectiveCount}</b><span>protectores</span></div></div><div className="vd-cambra__insight"><Stethoscope size={17} /><div><strong>{result.tone === "extreme" || result.tone === "high" ? "La evaluación requiere atención prioritaria" : result.tone === "low" ? "La prevención está bien encaminada" : "Hay factores modificables que conviene abordar"}</strong><span>Revisa los indicadores clínicos, el contexto del paciente y el balance de factores antes de confirmar el riesgo.</span></div></div><h3>Resumen para la consulta</h3><ul className="vd-cambra__summary-list"><li><CheckCircle2 size={15} /><span>{result.diseaseCount ? "Se marcaron indicadores de enfermedad; documenta hallazgos y considera pruebas complementarias según criterio clínico." : "No se marcaron indicadores de enfermedad en este formulario."}</span></li><li><CheckCircle2 size={15} /><span>{result.riskCount ? "Aborda los factores biológicos y ambientales positivos con metas específicas." : "No se marcaron factores biológicos o ambientales de riesgo."}</span></li><li><CheckCircle2 size={15} /><span>Define medidas protectoras, metas de autocuidado y fecha de reevaluación.</span></li></ul><div className="vd-cambra__steps"><div className="vd-cambra__step"><b>1</b><span>Explicar el balance de caries al paciente</span></div><div className="vd-cambra__step"><b>2</b><span>Definir medidas preventivas personalizadas</span></div><div className="vd-cambra__step"><b>3</b><span>Programar reevaluación y actualizar el riesgo</span></div></div><div className="vd-cambra__actions"><button className="vd-cambra__primary" type="button" onClick={saveEvaluation} disabled={saving}>{saving ? <Loader2 size={15} /> : <Save size={15} />}{saving ? "Guardando..." : "Guardar evaluación"}</button><button className="vd-cambra__secondary" type="button" onClick={copySummary}><Clipboard size={15} /> Copiar resumen</button><button className="vd-cambra__secondary" type="button" onClick={() => window.print()}><FileDown size={15} /> Imprimir</button>{saveMessage && <p className="vd-cambra__save-message" role="status">{saveMessage}</p>}</div></div>
        </aside>
      </div>
    </section>
  );
}
